import { useEffect, useLayoutEffect, useRef, useState, type ReactElement } from "react";
import { useLocation } from "react-router-dom";
import Intercom, { hide as hideIntercom, onHide, onShow, show as showIntercom, showConversation, showTicket, update as updateIntercom } from "@intercom/messenger-js-sdk";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { isSupportChatHidden, setSupportChatHidden, useSupportChatHidden } from "@/lib/support-chat";
import { helpMenuTestIds as ids } from "@/testids/helpMenuTestIds";
import { useAuth } from "../lib/auth";

const INTERCOM_APP_ID = "hf1th8qf";

// MCP hand-off links (/connect, /config-review, /test-case-review) render as a
// bare single-purpose popup, and setup links (/setup) are sent to a partner's
// own customers — no fastn support widget on either. Base-path safe: matches
// the segment anywhere so /gcp/connect/... is covered too.
const LINK_ROUTE_RE = /\/(connect|config-review|test-case-review|setup)\//;
const LAUNCHER_SELECTOR = ".intercom-lightweight-app-launcher, .intercom-launcher, iframe[name='intercom-launcher-frame']";

// GL1: the launcher is a fixed, very-high-z-index bottom-right bubble that
// lands on top of any panel whose action buttons live in a bottom-right
// footer (e.g. Create/Cancel on the trigger panels) — those buttons render
// but are unclickable underneath it. Fighting z-index per panel doesn't
// scale, so instead hide the launcher globally whenever such a footer is
// in the DOM, using Intercom's own `hide_default_launcher` toggle.
//
// Every footer class in the app is listed here, not just the slide-over one:
// the launcher outranks a centred modal too, so `.modal-footer` /
// `.ps-modal-footer` dialogs were still losing their Cancel button. Any new
// bottom-aligned action row must be added here.
export const FOOTER_SELECTOR = [
  ".admin-slideover-footer",
  ".modal-footer",
  ".ps-modal-footer",
  // Dialog and ConfirmDialog footers: their scrim sits far below the launcher,
  // so on a phone or a tall dialog it covers the right-aligned confirm button.
  ".pf-modal__f",
  // Bottom-pinned action in the onboarding sidebar ("Explore dashboard on
  // your own") — the launcher sat on its right edge for the whole setup flow.
  ".ob-skip",
  // Onboarding's agent chat panel and drawers keep their input and actions in
  // the bottom-right corner, under the launcher.
  ".aiside",
  ".ob3-sheet-scrim",
].join(",");

/** Asks the widget to appear and open. Fired by Help -> Live chat. */
export const OPEN_LIVE_CHAT_EVENT = "fastn:open-live-chat";

/**
 * Asks the widget to appear on one conversation; `detail` is its id. Fired by
 * the on-call escalation receipt, so the customer lands in the conversation the
 * engineer will answer instead of hunting for it under Contact support.
 */
export const OPEN_CONVERSATION_EVENT = "fastn:open-conversation";

/** The same for a ticket; `detail` is its id. The escalation receipt fires it when a ticket was opened. */
export const OPEN_TICKET_EVENT = "fastn:open-ticket";

export function IntercomWidget(): ReactElement | null {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const onLinkRoute = LINK_ROUTE_RE.test(pathname);
  /**
   * The launcher shows by default. Its close button hides it for this browser;
   * Help -> "Contact support" opens the chat and brings the launcher back.
   */
  const hidden = useSupportChatHidden();
  const [footerOpen, setFooterOpen] = useState(false);
  const [messengerOpen, setMessengerOpen] = useState(false);
  const [booted, setBooted] = useState(false);
  // The launcher Intercom actually rendered. Blocked by an ad blocker or CSP
  // there is none, so there must be no close button floating on its own either.
  const [launcher, setLauncher] = useState<Element | null>(null);
  const listening = useRef(false);
  // Who Intercom was last booted for. Booting again is only needed when the
  // person changes, not every time a link route is left.
  const bootedFor = useRef<string | null>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    // Opening the chat, on a conversation or ticket or not, also brings the bubble back.
    const open = (show: () => void) => {
      setSupportChatHidden(false);
      updateIntercom({ hide_default_launcher: false });
      show();
    };
    const onOpen = () => open(showIntercom);
    const detail = (e: Event) => (e as CustomEvent<string>).detail;
    const onOpenConversation = (e: Event) => open(() => (detail(e) ? showConversation(detail(e)) : showIntercom()));
    const onOpenTicket = (e: Event) => open(() => (detail(e) ? showTicket(detail(e)) : showIntercom()));
    window.addEventListener(OPEN_LIVE_CHAT_EVENT, onOpen);
    window.addEventListener(OPEN_CONVERSATION_EVENT, onOpenConversation);
    window.addEventListener(OPEN_TICKET_EVENT, onOpenTicket);
    return () => {
      window.removeEventListener(OPEN_LIVE_CHAT_EVENT, onOpen);
      window.removeEventListener(OPEN_CONVERSATION_EVENT, onOpenConversation);
      window.removeEventListener(OPEN_TICKET_EVENT, onOpenTicket);
    };
  }, []);

  useEffect(() => {
    if (!user?.email || onLinkRoute) return;
    const who = `${user.sub}|${user.email}|${user.name ?? ""}`;
    if (bootedFor.current === who) return;
    bootedFor.current = who;

    Intercom({
      app_id: INTERCOM_APP_ID,
      user_id: user.sub,
      name: user.name,
      email: user.email,
      // Decided at boot: an update sent before the messenger finishes loading
      // is dropped, so the starting state has to be right here.
      hide_default_launcher: isSupportChatHidden() || document.querySelector(FOOTER_SELECTOR) !== null,
    });
    if (!listening.current) {
      listening.current = true;
      onShow(() => setMessengerOpen(true));
      onHide(() => setMessengerOpen(false));
    }
    setBooted(true);
  }, [user?.email, user?.sub, user?.name, onLinkRoute]);

  useEffect(() => {
    if (!user?.email) return;
    // Coalesced to one check per frame: the observer fires on every DOM change
    // in the app, which is thousands a second while an agent streams.
    let frame = 0;
    const sync = () => {
      frame = 0;
      setFooterOpen(document.querySelector(FOOTER_SELECTOR) !== null);
      setLauncher(document.querySelector(LAUNCHER_SELECTOR));
    };
    sync();
    const observer = new MutationObserver(() => { if (!frame) frame = requestAnimationFrame(sync); });
    observer.observe(document.body, { childList: true, subtree: true });
    return () => { observer.disconnect(); cancelAnimationFrame(frame); };
  }, [user?.email]);

  const launcherShown = booted && !hidden && !footerOpen && !onLinkRoute;
  // A chat left open must not follow the person onto a link route either.
  useEffect(() => {
    if (booted && onLinkRoute) hideIntercom();
  }, [booted, onLinkRoute]);
  useEffect(() => {
    if (!booted) return;
    updateIntercom({ hide_default_launcher: !launcherShown });
  }, [booted, launcherShown]);

  const closeShown = launcherShown && launcher !== null && !messengerOpen;

  // Centred on the launcher's top-left edge, wherever Intercom put it and
  // whatever size it is, so a workspace that moves the launcher moves the X too.
  useLayoutEffect(() => {
    const btn = closeRef.current;
    if (!closeShown || !btn || !launcher) return;
    const place = () => {
      const r = launcher.getBoundingClientRect();
      if (r.width === 0) { btn.style.visibility = "hidden"; return; }
      const edge = Math.SQRT1_2;
      const cx = r.left + r.width / 2 - (r.width / 2) * edge;
      const cy = r.top + r.height / 2 - (r.height / 2) * edge;
      btn.style.left = `${Math.round(cx - btn.offsetWidth / 2)}px`;
      btn.style.top = `${Math.round(cy - btn.offsetHeight / 2)}px`;
      btn.style.visibility = "visible";
    };
    place();
    // Size changes, window changes, and moves: Intercom slides the launcher in
    // and repositions it through its style, neither of which resizes it.
    const resize = new ResizeObserver(place);
    resize.observe(launcher);
    const moved = new MutationObserver(place);
    moved.observe(launcher, { attributes: true, attributeFilter: ["style", "class"] });
    window.addEventListener("resize", place);
    launcher.addEventListener("transitionend", place);
    launcher.addEventListener("animationend", place);
    return () => {
      resize.disconnect();
      moved.disconnect();
      window.removeEventListener("resize", place);
      launcher.removeEventListener("transitionend", place);
      launcher.removeEventListener("animationend", place);
    };
  }, [closeShown, launcher]);

  if (!closeShown) return null;
  return (
    <Button
      ref={closeRef}
      variant="outline"
      size="icon"
      className="invisible fixed z-[2147483004] size-6 rounded-pill border-2 border-background bg-foreground p-0 text-background hover:bg-foreground hover:text-background pointer-coarse:size-8 [&_svg]:size-3.5"
      aria-label="Hide chat button"
      title="Hide chat button. Help, then Contact support, brings it back."
      onClick={() => setSupportChatHidden(true)}
      data-testid={ids.chatCloseButton}
    >
      <X aria-hidden />
    </Button>
  );
}
